import { useState, useEffect } from "react";
import axios from "axios";
import { useAuth } from "../../context/AuthContext";
import { normalizeError } from "../../lib/normalizeError";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Badge } from "../../components/ui/badge";
import { Checkbox } from "../../components/ui/checkbox";
import { Label } from "../../components/ui/label";
import { 
  Table, 
  TableBody, 
  TableCell, 
  TableHead, 
  TableHeader, 
  TableRow 
} from "../../components/ui/table";
import { ScrollArea } from "../../components/ui/scroll-area";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from "../../components/ui/dialog";
import { toast } from "sonner";
import { Users, Plus, Shield, Trash2, Edit2, AlertTriangle } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL || ''}/api`;

// P0.4: All available roles
const ALL_ROLES = ["admin", "purchasing", "receiving", "scanner", "viewer"];

// P0.4: Role checkboxes component (moved outside to avoid nested component error)
const RoleCheckboxes = ({ roles, onChange, prefix = "" }) => {
  const isAdmin = roles.includes("admin");
  
  return (
    <div className="grid grid-cols-2 gap-3 p-3 bg-muted/30 rounded-sm">
      {ALL_ROLES.map((role) => {
        const isDisabled = role !== "admin" && isAdmin;
        
        return (
          <div key={role} className="flex items-center space-x-2">
            <Checkbox
              id={`${prefix}role-${role}`}
              checked={roles.includes(role)}
              onCheckedChange={(checked) => onChange(role, checked)}
              disabled={isDisabled}
              data-testid={`${prefix}role-${role}-checkbox`}
            />
            <Label
              htmlFor={`${prefix}role-${role}`}
              className={`text-sm uppercase ${isDisabled ? "text-muted-foreground" : ""}`}
            >
              {role}
            </Label>
          </div>
        );
      })}
      {isAdmin && (
        <p className="col-span-2 text-xs text-yellow-400 mt-1">
          Admin has full access - other roles disabled
        </p>
      )}
    </div>
  );
};

export default function AdminUsers() {
  const { getAuthHeader, register, user: currentUser } = useAuth();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [addDialog, setAddDialog] = useState(false);
  const [editDialog, setEditDialog] = useState(null);
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [deleteInput, setDeleteInput] = useState("");
  const [newUser, setNewUser] = useState({ email: "", password: "", name: "", roles: ["viewer"] });
  const [editUser, setEditUser] = useState({ name: "", roles: [] });

  useEffect(() => {
    fetchUsers();
  }, []);

  const fetchUsers = async () => {
    try {
      const res = await axios.get(`${API}/users`, { withCredentials: true });
      setUsers(res.data);
    } catch (error) {
      toast.error("Failed to load users");
    } finally {
      setLoading(false);
    }
  };

  // P0.4: Handle role checkbox change
  const handleRoleChange = (roles, role, checked, setRoles) => {
    let newRoles;
    
    if (role === "admin") {
      // If ADMIN is checked, set only admin
      newRoles = checked ? ["admin"] : ["viewer"];
    } else {
      // Regular role change
      if (checked) {
        newRoles = [...roles.filter(r => r !== "admin"), role];
      } else {
        newRoles = roles.filter(r => r !== role);
        // Ensure at least one role
        if (newRoles.length === 0) {
          newRoles = ["viewer"];
        }
      }
    }
    
    setRoles(newRoles);
  };

  const handleAddUser = async () => {
    try {
      // Use admin endpoint to bypass invite-only checks
      await axios.post(`${API}/admin/users`, {
        email: newUser.email,
        password: newUser.password,
        name: newUser.name,
        roles: newUser.roles
      }, { withCredentials: true });
      toast.success("User created successfully");
      setAddDialog(false);
      setNewUser({ email: "", password: "", name: "", roles: ["viewer"] });
      fetchUsers();
    } catch (error) {
      toast.error(normalizeError(error.response?.data) || "Failed to create user");
    }
  };

  // P0.4: Update user with multi-role
  const handleUpdateUser = async () => {
    if (!editDialog) return;
    
    try {
      await axios.put(`${API}/users/${editDialog.id}`, {
        name: editUser.name,
        roles: editUser.roles
      }, { withCredentials: true });
      toast.success("User updated");
      setEditDialog(null);
      fetchUsers();
    } catch (error) {
      toast.error(normalizeError(error.response?.data) || "Failed to update user");
    }
  };

  const handleToggleActive = async (userId, active) => {
    try {
      await axios.put(`${API}/users/${userId}`, { active }, { withCredentials: true });
      toast.success(active ? "User activated" : "User deactivated");
      fetchUsers();
    } catch (error) {
      toast.error("Failed to update user");
    }
  };

  // P0.5: Handle user deletion
  const handleDelete = async () => {
    if (!deleteConfirm || deleteInput !== "DELETE") {
      toast.error("Type DELETE to confirm");
      return;
    }
    
    try {
      await axios.delete(`${API}/admin/users/${deleteConfirm.id}`, { withCredentials: true });
      toast.success(`User ${deleteConfirm.email} deleted`);
      setDeleteConfirm(null);
      setDeleteInput("");
      fetchUsers();
    } catch (error) {
      toast.error(normalizeError(error.response?.data) || "Failed to delete user");
    }
  };

  const openEditDialog = (user) => {
    setEditDialog(user);
    setEditUser({
      name: user.name,
      roles: user.roles || [user.role || "viewer"]
    });
  };

  const getRoleBadgeClass = (role) => {
    switch (role) {
      case "admin": return "bg-red-900/30 text-red-400 border-red-900";
      case "purchasing": return "bg-blue-900/30 text-blue-400 border-blue-900";
      case "receiving": return "bg-green-900/30 text-green-400 border-green-900";
      case "scanner": return "bg-yellow-900/30 text-yellow-400 border-yellow-900";
      default: return "bg-muted text-muted-foreground border-border";
    }
  };

  // Check if current user is admin
  const isCurrentUserAdmin = currentUser?.roles?.includes("admin") || currentUser?.role === "admin";

  return (
    <div className="flex-1 flex flex-col">
      {/* Header */}
      <div className="h-16 border-b border-border px-6 flex items-center justify-between bg-card">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 bg-primary/10 border border-primary/30 rounded-sm flex items-center justify-center">
            <Users className="w-5 h-5 text-primary" />
          </div>
          <div>
            <h1 className="font-heading text-2xl tracking-tight">USERS</h1>
            <p className="text-xs text-muted-foreground">Manage user accounts and roles</p>
          </div>
        </div>
        
        <Button onClick={() => setAddDialog(true)} className="rounded-sm uppercase tracking-wider" data-testid="add-user-btn">
          <Plus className="w-4 h-4 mr-2" />
          Add User
        </Button>
      </div>
      
      {/* Content */}
      <ScrollArea className="flex-1">
        <div className="p-6">
          <div className="industrial-card overflow-hidden">
            <Table>
              <TableHeader>
                <TableRow className="industrial-table-header">
                  <TableHead>NAME</TableHead>
                  <TableHead>EMAIL</TableHead>
                  <TableHead>ROLES</TableHead>
                  <TableHead>STATUS</TableHead>
                  <TableHead className="w-40">ACTIONS</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {loading ? (
                  <TableRow>
                    <TableCell colSpan={5} className="text-center py-8 text-muted-foreground">
                      Loading...
                    </TableCell>
                  </TableRow>
                ) : users.map((user) => (
                  <TableRow key={user.id} className="industrial-table-row" data-testid={`user-row-${user.email}`}>
                    <TableCell className="font-medium">{user.name}</TableCell>
                    <TableCell className="text-muted-foreground">{user.email}</TableCell>
                    <TableCell>
                      {/* P0.4: Show multiple role badges */}
                      <div className="flex flex-wrap gap-1">
                        {(user.roles || [user.role]).map((role) => (
                          <Badge 
                            key={role} 
                            className={`industrial-badge text-xs ${getRoleBadgeClass(role)}`}
                          >
                            {role.toUpperCase()}
                          </Badge>
                        ))}
                      </div>
                    </TableCell>
                    <TableCell>
                      <Badge className={`industrial-badge ${user.active ? "bg-green-900/30 text-green-400 border-green-900" : "bg-red-900/30 text-red-400 border-red-900"}`}>
                        {user.active ? "ACTIVE" : "INACTIVE"}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-1">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => openEditDialog(user)}
                          className="text-xs"
                          data-testid={`edit-user-${user.email}`}
                        >
                          <Edit2 className="w-4 h-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleToggleActive(user.id, !user.active)}
                          className="text-xs uppercase"
                          data-testid={`toggle-user-${user.email}`}
                        >
                          {user.active ? "Deactivate" : "Activate"}
                        </Button>
                        {/* P0.5: Delete button (not for self) */}
                        {isCurrentUserAdmin && user.id !== currentUser?.id && (
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => setDeleteConfirm(user)}
                            className="text-red-400 hover:text-red-300 hover:bg-red-900/20"
                            data-testid={`delete-user-${user.email}`}
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        )}
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </div>
      </ScrollArea>
      
      {/* Add User Dialog */}
      <Dialog open={addDialog} onOpenChange={setAddDialog}>
        <DialogContent className="bg-card border-border">
          <DialogHeader>
            <DialogTitle className="font-heading text-xl tracking-tight">
              ADD USER
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <label className="text-xs text-muted-foreground uppercase">Name</label>
              <Input
                value={newUser.name}
                onChange={(e) => setNewUser({ ...newUser, name: e.target.value })}
                className="industrial-input"
                data-testid="new-user-name"
              />
            </div>
            
            <div className="space-y-2">
              <label className="text-xs text-muted-foreground uppercase">Email</label>
              <Input
                type="email"
                value={newUser.email}
                onChange={(e) => setNewUser({ ...newUser, email: e.target.value })}
                className="industrial-input"
                data-testid="new-user-email"
              />
            </div>
            
            <div className="space-y-2">
              <label className="text-xs text-muted-foreground uppercase">Password</label>
              <Input
                type="password"
                value={newUser.password}
                onChange={(e) => setNewUser({ ...newUser, password: e.target.value })}
                className="industrial-input"
                data-testid="new-user-password"
              />
            </div>
            
            {/* P0.4: Role Checkboxes */}
            <div className="space-y-2">
              <label className="text-xs text-muted-foreground uppercase flex items-center gap-2">
                <Shield className="w-3 h-3" />
                Roles
              </label>
              <RoleCheckboxes
                roles={newUser.roles}
                onChange={(role, checked) => handleRoleChange(
                  newUser.roles, 
                  role, 
                  checked, 
                  (newRoles) => setNewUser({ ...newUser, roles: newRoles })
                )}
                prefix="new-"
              />
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="secondary" onClick={() => setAddDialog(false)} className="uppercase tracking-wider rounded-sm">
              Cancel
            </Button>
            <Button onClick={handleAddUser} className="uppercase tracking-wider rounded-sm" data-testid="confirm-add-user">
              Create User
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit User Dialog */}
      <Dialog open={!!editDialog} onOpenChange={(open) => !open && setEditDialog(null)}>
        <DialogContent className="bg-card border-border">
          <DialogHeader>
            <DialogTitle className="font-heading text-xl tracking-tight">
              EDIT USER
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <label className="text-xs text-muted-foreground uppercase">Name</label>
              <Input
                value={editUser.name}
                onChange={(e) => setEditUser({ ...editUser, name: e.target.value })}
                className="industrial-input"
                data-testid="edit-user-name"
              />
            </div>
            
            <div className="space-y-2">
              <label className="text-xs text-muted-foreground uppercase">Email (read-only)</label>
              <Input
                value={editDialog?.email || ""}
                disabled
                className="industrial-input"
              />
            </div>
            
            {/* P0.4: Role Checkboxes for Edit */}
            <div className="space-y-2">
              <label className="text-xs text-muted-foreground uppercase flex items-center gap-2">
                <Shield className="w-3 h-3" />
                Roles
              </label>
              <RoleCheckboxes
                roles={editUser.roles}
                onChange={(role, checked) => handleRoleChange(
                  editUser.roles, 
                  role, 
                  checked, 
                  (newRoles) => setEditUser({ ...editUser, roles: newRoles })
                )}
                prefix="edit-"
              />
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="secondary" onClick={() => setEditDialog(null)} className="uppercase tracking-wider rounded-sm">
              Cancel
            </Button>
            <Button onClick={handleUpdateUser} className="uppercase tracking-wider rounded-sm" data-testid="confirm-edit-user">
              Save Changes
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* P0.5: Delete Confirmation Dialog */}
      <Dialog open={!!deleteConfirm} onOpenChange={(open) => { if (!open) { setDeleteConfirm(null); setDeleteInput(""); } }}>
        <DialogContent className="bg-card border-border">
          <DialogHeader>
            <DialogTitle className="font-heading text-xl tracking-tight flex items-center gap-2 text-red-400">
              <AlertTriangle className="w-5 h-5" />
              DELETE USER
            </DialogTitle>
            <DialogDescription>
              This action cannot be undone. The user will be permanently removed from the system.
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div className="p-3 bg-red-900/20 border border-red-700/50 rounded-sm">
              <p className="text-sm">
                You are about to delete: <span className="font-bold text-red-400">{deleteConfirm?.email}</span>
              </p>
            </div>
            <div className="space-y-2">
              <label className="text-xs text-muted-foreground uppercase">Type DELETE to confirm</label>
              <Input
                value={deleteInput}
                onChange={(e) => setDeleteInput(e.target.value)}
                placeholder="DELETE"
                className="industrial-input font-mono"
                data-testid="delete-confirm-input"
              />
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="secondary" onClick={() => { setDeleteConfirm(null); setDeleteInput(""); }} className="uppercase tracking-wider rounded-sm">
              Cancel
            </Button>
            <Button 
              variant="destructive" 
              onClick={handleDelete} 
              disabled={deleteInput !== "DELETE"}
              className="uppercase tracking-wider rounded-sm"
              data-testid="confirm-delete-btn"
            >
              Delete User
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
